import React from 'react';
import { TrendingUp, TrendingDown, BarChart2, Layers, Shield, Zap } from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const TradingSection = () => {
  const [ref, isVisible] = useScrollAnimation({ threshold: 0.1 });

  const tradingTypes = [
    {
      id: 1,
      icon: BarChart2,
      title: 'Spot Trading',
      description: 'Direct asset purchases with precise entry and exit timing across major pairs.'
    },
    {
      id: 2,
      icon: Layers,
      title: 'Options Trading',
      description: 'Strategic options positions for hedging and enhanced returns in any market condition.'
    },
    {
      id: 3,
      icon: Zap,
      title: 'Derivatives',
      description: 'Futures and perpetuals trading with disciplined leverage and risk controls.'
    }
  ];

  return (
    <section ref={ref} className="relative bg-black py-16 md:py-24 lg:py-32 overflow-hidden">
      {/* Background Trading Image */}
      <div className="absolute inset-0">
        <img 
          src="https://images.unsplash.com/photo-1642790106117-e829e14a795f?q=80&w=2940&auto=format&fit=crop"
          alt=""
          className="w-full h-full object-cover opacity-[0.06]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/95 to-black/90" />
      </div>
      
      {/* Ambient Effects */}
      <div className="absolute top-0 right-1/4 w-[400px] md:w-[600px] h-[400px] md:h-[600px] bg-gradient-radial from-blue-900/10 via-transparent to-transparent blur-3xl" />
      
      <div className="relative z-10 max-w-[1600px] mx-auto px-4 md:px-8 lg:px-16">
        {/* Header */}
        <div 
          className="mb-10 md:mb-16"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1)'
          }}
        >
          <p className="text-white/30 text-[10px] md:text-xs font-medium tracking-[0.2em] md:tracking-[0.3em] mb-3 md:mb-4">
            [ HOW WE TRADE ]
          </p>
          <h2 className="text-2xl md:text-3xl lg:text-5xl font-light text-white leading-tight tracking-tight mb-4 md:mb-6">
            Institutional-Grade Trading
          </h2>
          <p className="text-white/50 text-xs md:text-base lg:text-lg font-light max-w-2xl leading-relaxed">
            We trade across crypto and gold — Bitcoin, Ethereum, Solana and tokenized gold (PAXG) — executing in spot, options, and derivatives markets. Whether the market pumps or dumps, we never panic — we trade like institutions and apply strategy accordingly.
          </p>
        </div>

        {/* Market Condition Banner */}
        <div 
          className="mb-10 md:mb-16 bg-gradient-to-r from-green-500/5 via-transparent to-red-500/5 border border-white/[0.06] rounded-lg p-4 md:p-6 lg:p-8"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1) 200ms'
          }}
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-8">
            {/* Bull Market */}
            <div className="flex items-center gap-3 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-green-500/10 border border-green-500/30 rounded-full flex items-center justify-center">
                <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-green-400" />
              </div>
              <div>
                <p className="text-green-400 text-sm md:text-base font-medium">Market Pumps</p>
                <p className="text-white/40 text-[10px] md:text-xs">We capitalize strategically</p>
              </div>
            </div>

            {/* Center Message */}
            <div className="text-center py-3 md:py-0">
              <p className="text-white/80 text-sm md:text-lg font-light">
                <span className="text-amber-400">We Never Panic</span>
              </p>
              <p className="text-white/40 text-[10px] md:text-xs mt-1">Disciplined execution in all conditions</p>
            </div>

            {/* Bear Market */}
            <div className="flex items-center gap-3 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-red-500/10 border border-red-500/30 rounded-full flex items-center justify-center">
                <TrendingDown className="w-5 h-5 md:w-6 md:h-6 text-red-400" />
              </div>
              <div>
                <p className="text-red-400 text-sm md:text-base font-medium">Market Dumps</p>
                <p className="text-white/40 text-[10px] md:text-xs">We hedge and protect</p>
              </div>
            </div>
          </div>
        </div>

        {/* Trading Types Grid */}
        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {tradingTypes.map((type, index) => {
            const IconComponent = type.icon;
            return (
              <div
                key={type.id}
                className="group relative"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
                  transition: `all 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${300 + index * 100}ms`
                }}
              >
                <div className="absolute -inset-0.5 bg-gradient-to-b from-white/10 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-sm" />
                
                <div className="relative h-full bg-white/[0.02] border border-white/[0.06] rounded-lg p-5 md:p-6 lg:p-8 group-hover:border-white/15 transition-all duration-500 card-lift">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-white/[0.03] border border-white/10 rounded-lg flex items-center justify-center mb-4 md:mb-6 group-hover:bg-amber-500/10 group-hover:border-amber-500/30 transition-all duration-500">
                    <IconComponent className="w-5 h-5 md:w-6 md:h-6 text-white/60 group-hover:text-amber-400 transition-colors duration-500" />
                  </div>
                  <h3 className="text-base md:text-lg lg:text-xl font-light text-white mb-2 md:mb-3">
                    {type.title}
                  </h3>
                  <p className="text-white/40 text-[11px] md:text-sm font-light leading-relaxed">
                    {type.description}
                  </p>
                </div>
              </div>
            );
          })}        </div>

        {/* Institutional Approach */}
        <div 
          className="mt-10 md:mt-16 flex flex-col md:flex-row items-center justify-between gap-4 md:gap-6 bg-amber-500/5 border border-amber-500/20 rounded-lg p-4 md:p-6 lg:p-8"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 1s cubic-bezier(0.16, 1, 0.3, 1) 600ms'
          }}
        >
          <div className="flex items-center gap-3 md:gap-4">
            <Shield className="w-6 h-6 md:w-8 md:h-8 text-amber-500" />
            <div>
              <p className="text-white text-sm md:text-base lg:text-lg font-light">
                We trade as <span className="text-amber-400 font-medium">institutions</span> and apply strategy accordingly
              </p>
              <p className="text-white/40 text-[10px] md:text-xs mt-1">
                Risk-managed, rule-based execution across all market conditions
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TradingSection;
